import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import catchError from '../../../components/global/CatchError'
import Filter, { FilterConfig } from '../../../components/global/Filter'
import Loader from '../../../components/global/Loader'
import Title from '../../../components/global/Title'
import { sweetAlertSuccess } from '../../../components/sweetalert/Alert'
import Table from '../../../components/table/Table'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { useVerifyAdminToken } from '../../../hooks/useVerifyAdminToken'
import apiClient from '../../../services/apiClient'
import { AdminContainer, LinkBtn, TitleWrapper } from '../users/AdminUsers'

type JobStatus = 'pending' | 'processing' | 'completed' | 'failed'

interface Job {
    id: number
    action: string
    payload: any
    status: JobStatus
    attempts: number
    createdAt: string
    updatedAt: string
    scheduledAt?: string
    lastError?: string
}

interface QueueStats {
    pending: number
    processing: number
    completed: number
    failed: number
    total: number
    paused: boolean
}

const AdminBadnetQueue = () => {
    const user = useContext(UserContext)
    const [jobs, setJobs] = useState<Job[]>([])
    const [stats, setStats] = useState<QueueStats | null>(null)
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [actionFilter, setActionFilter] = useState<string>('all')
    const [searchFilter, setSearchFilter] = useState<string>('')
    const [loading, setLoading] = useState(true)
    const [selectedJob, setSelectedJob] = useState<Job | null>(null)
    const [processing, setProcessing] = useState(false)

    const verifyToken = useVerifyAdminToken()
    const lng = useLangSelected()
    const { t } = useTranslation('translation')

    const filterConfig: FilterConfig = useMemo(
        () => ({
            category: {
                field: 'status',
                label: t('admin.badnet.queue.filter.status'),
                options: ['pending', 'processing', 'completed', 'failed'],
            },
            select: [
                {
                    field: 'action',
                    label: t('admin.badnet.queue.filter.action'),
                    options: [
                        { value: 'sync_tournaments', label: t('admin.badnet.queue.actions.syncTournaments') },
                        { value: 'sync_tournament', label: t('admin.badnet.queue.actions.syncTournament') },
                        { value: 'update_member_count', label: t('admin.badnet.queue.actions.updateMemberCount') },
                    ],
                },
            ],
            search: {
                placeholder: t('admin.badnet.queue.filter.searchPlaceholder'),
            },
        }),
        [t],
    )

    const loadStats = () => {
        apiClient
            .get('/badnet/queue/status')
            .then(({ data }) => {
                setStats(data.data)
            })
            .catch((err) => {
                console.error('Error loading stats:', err)
                catchError(err.response?.data || 'Erreur lors du chargement des statistiques', () => {
                    localStorage.removeItem('tokenAdmin')
                    user.adminData = undefined
                    window.location.reload()
                })
            })
    }

    const loadJobs = useCallback(() => {
        setLoading(true)
        const params: any = { limit: 50 }
        if (statusFilter && statusFilter !== 'all') {
            params.status = statusFilter
        }

        apiClient
            .get('/badnet/queue/jobs', { params })
            .then(({ data }) => {
                let filteredJobs = data.data
                if (actionFilter && actionFilter !== 'all') {
                    filteredJobs = filteredJobs.filter((job: Job) => job.action === actionFilter)
                }
                if (searchFilter) {
                    const searchLower = searchFilter.toLowerCase()
                    filteredJobs = filteredJobs.filter((job: Job) =>
                        JSON.stringify(job.payload).toLowerCase().includes(searchLower),
                    )
                }
                setJobs(filteredJobs)
            })
            .catch((err) => {
                console.error('Error loading jobs:', err)
                catchError(err.response?.data || 'Erreur lors du chargement des jobs', () => {
                    localStorage.removeItem('tokenAdmin')
                    user.adminData = undefined
                    window.location.reload()
                })
            })
            .finally(() => setLoading(false))
    }, [statusFilter, actionFilter, searchFilter])

    const handleFiltersChange = (newFilters: Record<string, any>) => {
        if (newFilters.status !== undefined) setStatusFilter(newFilters.status)
        if (newFilters.action !== undefined) setActionFilter(newFilters.action)
        if (newFilters.search !== undefined) setSearchFilter(newFilters.search)
    }

    useEffect(() => {
        verifyToken()
        loadStats()
        loadJobs()
    }, [loadJobs])

    const pauseQueue = async () => {
        verifyToken()
        try {
            await apiClient.post('/badnet/queue/pause')
            sweetAlertSuccess(t('admin.badnet.queue.pauseSuccess'))
            loadStats()
        } catch (error: any) {
            catchError(error.response?.data || 'Erreur lors de la mise en pause', () => {
                localStorage.removeItem('tokenAdmin')
                user.adminData = undefined
                window.location.reload()
            })
        }
    }

    const resumeQueue = async () => {
        verifyToken()
        try {
            await apiClient.post('/badnet/queue/resume')
            sweetAlertSuccess(t('admin.badnet.queue.resumeSuccess'))
            loadStats()
        } catch (error: any) {
            catchError(error.response?.data || 'Erreur lors de la reprise', () => {
                localStorage.removeItem('tokenAdmin')
                user.adminData = undefined
                window.location.reload()
            })
        }
    }

    const deleteOldJobs = async () => {
        verifyToken()
        try {
            const response = await apiClient.delete('/badnet/queue/old-jobs', { params: { days: 30 } })
            sweetAlertSuccess(`${response.data.data.count} ${t('admin.badnet.queue.deleteOldSuccess')}`)
            loadStats()
            loadJobs()
        } catch (error: any) {
            catchError(error.response?.data || 'Erreur lors de la suppression', () => {
                localStorage.removeItem('tokenAdmin')
                user.adminData = undefined
                window.location.reload()
            })
        }
    }

    const processJobManually = async (jobId: number) => {
        verifyToken()
        setProcessing(true)
        try {
            await apiClient.post(`/badnet/queue/process-job/${jobId}`)
            sweetAlertSuccess(t('admin.badnet.queue.processJobSuccess'))
            loadStats()
            loadJobs()
        } catch (error: any) {
            catchError(error.response?.data || "Erreur lors de l'exécution", () => {
                localStorage.removeItem('tokenAdmin')
                user.adminData = undefined
                window.location.reload()
            })
        } finally {
            setProcessing(false)
        }
    }

    const deleteById = (id: number) => {
        verifyToken()
        apiClient
            .delete(`/badnet/queue/jobs/${id}`)
            .then(() => {
                sweetAlertSuccess('La suppression de ce job est réussie').then(() => {
                    setJobs(jobs.filter((job) => job.id !== id))
                    loadStats()
                })
            })
            .catch((err) =>
                catchError(err.response?.data || { status: 500, message: 'Une erreur est survenue' }, () => {
                    localStorage.removeItem('tokenAdmin')
                    user.adminData = undefined
                    window.location.reload()
                }),
            )
    }

    const getStatusColor = (status: JobStatus) => {
        switch (status) {
            case 'pending':
                return '#FFA500'
            case 'processing':
                return '#2196F3'
            case 'completed':
                return '#4CAF50'
            case 'failed':
                return '#F44336'
            default:
                return '#9E9E9E'
        }
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('fr-FR')
    }

    const tableHeader = ['#', 'Action', 'Statut', 'Tentatives', 'Créé le', '', '', '']

    const tableData = jobs.map((job) => ({
        id: job.id,
        action: job.action,
        status: (
            <span
                style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    backgroundColor: getStatusColor(job.status),
                    color: 'white',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                }}
            >
                {job.status}
            </span>
        ),
        attempts: job.attempts,
        createdAt: formatDate(job.createdAt),
        details: (
            <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setSelectedJob(job)}
                style={{ marginRight: '0.5rem', fontSize: '1.6rem' }}
            >
                {t('common.details')}
            </button>
        ),
        execute:
            job.status === 'pending' ? (
                <button
                    className="btn btn-sm btn-primary"
                    onClick={() => processJobManually(job.id)}
                    disabled={processing}
                    style={{ marginRight: '0.5rem', fontSize: '1.6rem' }}
                >
                    {t('admin.badnet.queue.execute')}
                </button>
            ) : null,
        edit: `/${lng}/bva-admin/badnet-queue/${job.id}`,
        remove: true,
    }))

    if (loading && !stats) return <Loader />

    return (
        <>
            <TitleWrapper>
                <Title dataTestId="admin-badnet-queue-title">{t('admin.badnet.queue.title')}</Title>
                <div className="d-flex gap-2 mb-2 flex-wrap">
                    {stats?.paused ? (
                        <button
                            className="btn btn-success"
                            onClick={resumeQueue}
                            style={{
                                fontSize: '1.6rem',
                            }}
                        >
                            {t('admin.badnet.queue.resume')}
                        </button>
                    ) : (
                        <button
                            className="btn btn-warning"
                            onClick={pauseQueue}
                            style={{
                                fontSize: '1.6rem',
                            }}
                        >
                            {t('admin.badnet.queue.pause')}
                        </button>
                    )}
                    <button
                        className="btn btn-danger"
                        onClick={deleteOldJobs}
                        style={{
                            fontSize: '1.6rem',
                        }}
                    >
                        {t('admin.badnet.queue.deleteOld')}
                    </button>
                    <LinkBtn
                        to={`/${lng}/bva-admin/badnet-queue/creer`}
                        className="btn btn-primary mb-2"
                        style={{
                            fontSize: '1.6rem',
                        }}
                    >
                        {t('admin.badnet.queue.createJob')}
                    </LinkBtn>
                </div>
                {stats && (
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                        <div
                            style={{
                                flex: 1,
                                minWidth: '120px',
                                textAlign: 'center',
                                padding: '1rem',
                                background: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <div style={{ fontSize: '1.2rem', color: '#666', marginBottom: '0.5rem' }}>
                                {t('admin.badnet.queue.stats.total')}
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>{stats.total}</div>
                        </div>
                        <div
                            style={{
                                flex: 1,
                                minWidth: '120px',
                                textAlign: 'center',
                                padding: '1rem',
                                background: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <div style={{ fontSize: '1.2rem', color: '#666', marginBottom: '0.5rem' }}>
                                {t('admin.badnet.queue.stats.pending')}
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#FFA500' }}>
                                {stats.pending}
                            </div>
                        </div>
                        <div
                            style={{
                                flex: 1,
                                minWidth: '120px',
                                textAlign: 'center',
                                padding: '1rem',
                                background: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <div style={{ fontSize: '1.2rem', color: '#666', marginBottom: '0.5rem' }}>
                                {t('admin.badnet.queue.stats.processing')}
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2196F3' }}>
                                {stats.processing}
                            </div>
                        </div>
                        <div
                            style={{
                                flex: 1,
                                minWidth: '120px',
                                textAlign: 'center',
                                padding: '1rem',
                                background: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <div style={{ fontSize: '1.2rem', color: '#666', marginBottom: '0.5rem' }}>
                                {t('admin.badnet.queue.stats.completed')}
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4CAF50' }}>
                                {stats.completed}
                            </div>
                        </div>
                        <div
                            style={{
                                flex: 1,
                                minWidth: '120px',
                                textAlign: 'center',
                                padding: '1rem',
                                background: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <div style={{ fontSize: '1.2rem', color: '#666', marginBottom: '0.5rem' }}>
                                {t('admin.badnet.queue.stats.failed')}
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#F44336' }}>{stats.failed}</div>
                        </div>
                        <div
                            style={{
                                flex: 1,
                                minWidth: '120px',
                                textAlign: 'center',
                                padding: '1rem',
                                background: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <div style={{ fontSize: '1.2rem', color: '#666', marginBottom: '0.5rem' }}>
                                {t('admin.badnet.queue.stats.paused')}
                            </div>
                            <div
                                style={{
                                    fontSize: '2rem',
                                    fontWeight: 'bold',
                                    color: stats.paused ? '#F44336' : '#4CAF50',
                                }}
                            >
                                {stats.paused ? t('common.yes') : t('common.no')}
                            </div>
                        </div>
                    </div>
                )}
            </TitleWrapper>
            <Filter config={filterConfig} onFiltersChange={handleFiltersChange} />
            <AdminContainer>
                {loading ? (
                    <Loader />
                ) : (
                    <Table tableData={tableData} tableHeader={tableHeader} deleteById={deleteById} />
                )}
            </AdminContainer>

            {/* Job Details Modal */}
            {selectedJob && (
                <div
                    className="modal show"
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                    tabIndex={-1}
                >
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" style={{ fontSize: '2rem' }}>
                                    {t('admin.badnet.queue.jobDetailsTitle')} #{selectedJob.id}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setSelectedJob(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div
                                    style={{
                                        marginBottom: '1rem',
                                        padding: '0.5rem',
                                        background: '#f9f9f9',
                                        borderRadius: '4px',
                                    }}
                                >
                                    <strong>{t('admin.badnet.queue.table.action')}:</strong> {selectedJob.action}
                                </div>
                                <div
                                    style={{
                                        marginBottom: '1rem',
                                        padding: '0.5rem',
                                        background: '#f9f9f9',
                                        borderRadius: '4px',
                                    }}
                                >
                                    <strong>{t('admin.badnet.queue.table.status')}:</strong>{' '}
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            backgroundColor: getStatusColor(selectedJob.status),
                                            color: 'white',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {selectedJob.status}
                                    </span>
                                </div>
                                <div
                                    style={{
                                        marginBottom: '1rem',
                                        padding: '0.5rem',
                                        background: '#f9f9f9',
                                        borderRadius: '4px',
                                    }}
                                >
                                    <strong>{t('admin.badnet.queue.table.attempts')}:</strong> {selectedJob.attempts}
                                </div>
                                <div
                                    style={{
                                        marginBottom: '1rem',
                                        padding: '0.5rem',
                                        background: '#f9f9f9',
                                        borderRadius: '4px',
                                    }}
                                >
                                    <strong>{t('admin.badnet.queue.table.createdAt')}:</strong>{' '}
                                    {formatDate(selectedJob.createdAt)}
                                </div>
                                <div
                                    style={{
                                        marginBottom: '1rem',
                                        padding: '0.5rem',
                                        background: '#f9f9f9',
                                        borderRadius: '4px',
                                    }}
                                >
                                    <strong>{t('admin.badnet.queue.table.updatedAt')}:</strong>{' '}
                                    {formatDate(selectedJob.updatedAt)}
                                </div>
                                {selectedJob.scheduledAt && (
                                    <div
                                        style={{
                                            marginBottom: '1rem',
                                            padding: '0.5rem',
                                            background: '#f9f9f9',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <strong>{t('admin.badnet.queue.table.scheduledAt')}:</strong>{' '}
                                        {formatDate(selectedJob.scheduledAt)}
                                    </div>
                                )}
                                {selectedJob.lastError && (
                                    <div
                                        style={{
                                            marginBottom: '1rem',
                                            padding: '0.5rem',
                                            background: '#f9f9f9',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <strong>{t('admin.badnet.queue.table.lastError')}:</strong>{' '}
                                        <span style={{ color: 'red' }}>{selectedJob.lastError}</span>
                                    </div>
                                )}
                                <div
                                    style={{
                                        marginBottom: '1rem',
                                        padding: '0.5rem',
                                        background: '#f9f9f9',
                                        borderRadius: '4px',
                                    }}
                                >
                                    <strong>{t('admin.badnet.queue.table.payload')}:</strong>
                                    <pre
                                        style={{
                                            fontSize: '1rem',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            marginTop: '0.5rem',
                                        }}
                                    >
                                        {JSON.stringify(selectedJob.payload, null, 2)}
                                    </pre>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setSelectedJob(null)}
                                >
                                    {t('common.close')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default AdminBadnetQueue
